/**
 * GlobalDeclareSpatialColumnDialog (T18)
 *
 * App-level mount for `DeclareSpatialColumnDialog`. Driven by the global Zustand
 * store; any surface (grid header menu, header glyph, MetaInfo manager row) opens
 * it via `openDeclareSpatialColumnDialog(...)`.
 *
 * On confirm:
 *   1. Persist the declaration via `addSpatialColumn`.
 *   2. Invalidate the MetaInfo query so subscribers re-read.
 *   3. Fire `feature:spatial_declare { format, trigger }` analytics.
 *   4. Close the dialog.
 * On remove (details mode): `removeSpatialColumn` → invalidate → close.
 */

import { useCallback } from 'react';
import {
  DeclareSpatialColumnDialog,
  type SpatialDeclareFormValues,
} from './DeclareSpatialColumnDialog';
import { useDeclareSpatialColumnDialogStore } from './declareSpatialColumnDialogStore';
import { useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import { addSpatialColumn, removeSpatialColumn } from '../../services/metaInfoApi';
import type { SpatialColumnDeclaration } from '../../services/metaInfoApi';
import { useAnalytics } from '../../../../../core/analytics';

const newDeclarationId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `spatial-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function GlobalDeclareSpatialColumnDialog() {
  const { isOpen, context, closeDialog } = useDeclareSpatialColumnDialogStore();
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();

  const handleConfirm = useCallback(
    async (values: SpatialDeclareFormValues) => {
      if (!context) return;
      const declaration: SpatialColumnDeclaration = {
        id: newDeclarationId(),
        schema: context.schema,
        table: context.table,
        column: context.column,
        format: values.format,
        ...(values.format === 'lat-lng-pair' ? { lngColumn: values.lngColumn } : {}),
        ...(values.format === 'latlng-text' ? { axisOrder: values.axisOrder } : {}),
        createdAt: new Date().toISOString(),
      };
      await addSpatialColumn(context.connectionId, context.database, declaration);
      track('feature:spatial_declare', {
        format: values.format,
        trigger: context.trigger,
      });
      await invalidate(context.connectionId, context.database);
      closeDialog();
    },
    [context, invalidate, track, closeDialog],
  );

  const handleRemove = useCallback(async () => {
    if (!context?.existing) return;
    await removeSpatialColumn(context.connectionId, context.database, context.existing.id);
    await invalidate(context.connectionId, context.database);
    closeDialog();
  }, [context, invalidate, closeDialog]);

  if (!isOpen || !context) return null;

  return (
    <DeclareSpatialColumnDialog
      open
      schema={context.schema}
      table={context.table}
      column={context.column}
      dataType={context.dataType}
      siblingColumns={context.siblingColumns}
      existing={context.existing}
      onConfirm={handleConfirm}
      onRemove={handleRemove}
      onCancel={closeDialog}
    />
  );
}
